import type { EventListViewModel } from "src/http/types";
import type { AppContext } from "src/shared/context/AppContext";
import { sendAnalyticsEvent } from "src/shared/utils/analyticsevent/analytics-event";
import { buildAnalyticsEvent } from "src/shared/utils/analyticsevent/build-analytics-event";
import { type Location, LocationSourceType } from "src/types/shared/cookies";
import { haversineDistance } from "src/website-apps/shared/components/location-selector/utils";
import { PrimaryRadius } from "src/website-apps/shared/constants/grid-radius";
import {
  getPerformerGridRoleSlots,
  getPerformerPrimaryCountryName,
} from "src/website-apps/shared/utils/performerGridSurface";

enum PerformerLocationAnalyticsEvents {
  LOCATION_AT_PAGE_LANDING = "PerformerLocationAtPageLanding",
  LOCATION_CHANGE = "PerformerLocationChange",
}

enum EffectiveLocationSource {
  /** IP geolocation — no cookie, override, or SEO city; location resolved from user's IP address */
  IP = "IP",
  /** Browser geolocation API — user granted GPS/device location permission */
  DEVICE = "DEVICE",
  /** User manually searched and selected a city via the location selector UI */
  USER_SELECTION = "USER_SELECTION",
  /** Location passed as a URL query parameter (e.g., ?city=...) without a backend override */
  QUERY_PARAM = "QUERY_PARAM",
  /** User's saved home location from their profile/cookie */
  HOME = "HOME",
  /** SEO city URL — performer page with city in the path (e.g., /performer/taylor-swift-tickets/new-york/) */
  SEO = "SEO",
  /** Backend override — server auto-set the location (e.g., a sports team's home city) */
  LOCATION_OVERRIDE = "LOCATION_OVERRIDE",
}

/**
 * Resolves which location source determined the local grid results.
 * Priority: SEO city (from URL path) \> backend override \> client-side location source
 *
 * @param model - The performer page model containing seoCity and other backend data
 * @param location - The client-side resolved location (IP, GPS, user selection, query param, home, or SEO cookie)
 * @param locationOverride - Backend-initiated location override (e.g., team home city)
 * @returns The winning location source, or null if no location is available
 */
const getEffectiveLocationSource = (
  model: EventListViewModel,
  location: Location | null,
  locationOverride?: Location,
): EffectiveLocationSource | null => {
  if (model.seoCity) return EffectiveLocationSource.SEO;
  if (locationOverride) return EffectiveLocationSource.LOCATION_OVERRIDE;

  switch (location?.src) {
    case LocationSourceType.IP: {
      return EffectiveLocationSource.IP;
    }
    case LocationSourceType.DEVICE: {
      return EffectiveLocationSource.DEVICE;
    }
    case LocationSourceType.USER_SELECTION: {
      return EffectiveLocationSource.USER_SELECTION;
    }
    case LocationSourceType.QUERY_PARAM: {
      return EffectiveLocationSource.QUERY_PARAM;
    }
    case LocationSourceType.HOME: {
      return EffectiveLocationSource.HOME;
    }
    case LocationSourceType.SEO: {
      return EffectiveLocationSource.SEO;
    }
    default: {
      return null;
    }
  }
};

interface PerformerLandingUAEParams {
  appContext: AppContext;
  env: AppContext["env"];
  model: EventListViewModel;
  location: Location | null;
  locationOverride?: Location | undefined;
}

/**
 * Tracks comprehensive location data when a user lands on a performer page.
 * This provides baseline analytics for understanding how users arrive at the page
 * and what location context they have at the start of their journey.
 *
 * Triggered when: User lands on performer page (after location modal is shown if applicable)
 *
 * Data recorded:
 * - All location sources (IP, GPS, user selection, URL params, SEO, paid search, system overrides)
 * - Effective location source: which of the above actually determined the local grid results
 * - Dynamic local grid radius (based on user's location and event density)
 * - Event counts across all grids (primary/local, global, international, country-specific)
 * - Total available events for the performer
 * - Page type flags (single grid only, city performer page)
 * - Country grid information if applicable
 * - Category information (ID and name of the performer)
 *
 * This data helps answer questions like:
 * - What location sources are most commonly used on page landing?
 * - How does the dynamic radius calculation affect event discovery?
 * - Are users landing with appropriate location context for their needs?
 * - How many events are typically available in each grid type?
 * - What percentage of users land in country grid mode?
 */
export const sendPerformerLandingUAE = ({
  appContext,
  env,
  model,
  location,
  locationOverride,
}: PerformerLandingUAEParams) => {
  const localGridEffectiveLocationSource = getEffectiveLocationSource(model, location, locationOverride);
  const { primaryGrid, restGrid: globalGrid } = getPerformerGridRoleSlots(model.performerGridSurface);

  const primaryGridEvents = primaryGrid?.totalCount ?? 0;
  const primaryGridFirstName = primaryGrid?.items?.[0]?.name ?? null;
  const primaryGridEverySameName = primaryGridFirstName
    ? (primaryGrid?.items?.every((event) => event.name === primaryGridFirstName) ?? false)
    : false;
  const globalGridEvents = globalGrid?.totalCount ?? 0;
  const globalGridFirstName = globalGrid?.items?.[0]?.name ?? null;
  const globalGridEverySameName = globalGridFirstName
    ? (globalGrid?.items?.every((event) => event.name === globalGridFirstName) ?? false)
    : false;
  const totalEvents = model.totalUnfilteredEventsCount;

  const isSingleGridOnly = globalGridEvents === 0 && primaryGridEvents > 0;
  const isCityPerformerPage = !!model.seoCity;

  const countryName = getPerformerPrimaryCountryName(model.performerGridSurface);
  const countryGridEvents = countryName ? primaryGridEvents : null;

  const dynamicLocalGridRadius = model.dynamicLocalGridRadiusInMeters ?? PrimaryRadius;

  const analyticsEventBody = {
    ...buildAnalyticsEvent(appContext, PerformerLocationAnalyticsEvents.LOCATION_AT_PAGE_LANDING),
    categoryId: model.categorySummary.categoryId,
    categoryName: model.categorySummary.categoryName,

    IpCity_Name: model.ipLocation?.cityName ?? null,
    IpCity_Lat: model.ipLocation?.latitude ?? null,
    IpCity_Long: model.ipLocation?.longitude ?? null,
    IpCity_Country: model.ipLocation?.countryName ?? null,
    IpCity_State: model.ipLocation?.stateProvinceName ?? null,

    GpsCity_Name: location?.src === LocationSourceType.DEVICE ? location.n : null,
    GpsCity_Lat: location?.src === LocationSourceType.DEVICE ? location.lt : null,
    GpsCity_Long: location?.src === LocationSourceType.DEVICE ? location.lg : null,
    GpsCity_Country: location?.src === LocationSourceType.DEVICE ? (location.ct ?? null) : null,
    GpsCity_State: location?.src === LocationSourceType.DEVICE ? (location.st ?? null) : null,

    UserSearchedCity_Name: location?.src === LocationSourceType.USER_SELECTION ? location.n : null,
    UserSearchedCity_Lat: location?.src === LocationSourceType.USER_SELECTION ? location.lt : null,
    UserSearchedCity_Long: location?.src === LocationSourceType.USER_SELECTION ? location.lg : null,
    UserSearchedCity_Country: location?.src === LocationSourceType.USER_SELECTION ? (location.ct ?? null) : null,
    UserSearchedCity_State: location?.src === LocationSourceType.USER_SELECTION ? (location.st ?? null) : null,

    QueryParamCity_Name: model.locationOverride?.city?.cityName ?? null,
    QueryParamCity_Lat: model.locationOverride?.city?.latitude ?? null,
    QueryParamCity_Long: model.locationOverride?.city?.longitude ?? null,
    QueryParamCity_Country: model.locationOverride?.city?.countryName ?? null,
    QueryParamCity_State: model.locationOverride?.city?.stateProvinceName ?? null,

    LocationOverrideCity_Name: locationOverride?.n ?? null,
    LocationOverrideCity_Lat: locationOverride?.lt ?? null,
    LocationOverrideCity_Long: locationOverride?.lg ?? null,
    LocationOverrideCity_Country: locationOverride?.ct ?? null,
    LocationOverrideCity_State: locationOverride?.st ?? null,

    SeoCity_Name: model.seoCity?.cityName ?? null,
    SeoCity_Lat: model.seoCity?.latitude ?? null,
    SeoCity_Long: model.seoCity?.longitude ?? null,
    SeoCity_Country: model.seoCity?.countryName ?? null,
    SeoCity_State: model.seoCity?.stateProvinceName ?? null,

    PaidSearchCity_Name: model.paidSearchQueryCity?.cityName ?? null,
    PaidSearchCity_Lat: model.paidSearchQueryCity?.latitude ?? null,
    PaidSearchCity_Long: model.paidSearchQueryCity?.longitude ?? null,
    PaidSearchCity_Country: model.paidSearchQueryCity?.countryName ?? null,
    PaidSearchCity_State: model.paidSearchQueryCity?.stateProvinceName ?? null,

    CountryGridName: countryName,

    dynamicLocalGridRadius,
    primaryGridEvents,
    primaryGridEverySameName,
    globalGridEvents,
    globalGridEverySameName,
    totalEvents,
    countryGridEvents,
    isSingleGridOnly,
    isCityPerformerPage,
    localGridEffectiveLocationSource,
  };

  sendAnalyticsEvent(appContext, env, PerformerLocationAnalyticsEvents.LOCATION_AT_PAGE_LANDING, [analyticsEventBody]);
};

interface LocationChangeUAEParams {
  appContext: AppContext;
  env: AppContext["env"];
  prevLocation: Location;
  location: Location;
  model: EventListViewModel;
}

/**
 * Tracks when a user changes their location on the performer page after initial page load.
 * This helps analyze user behavior patterns around location selection and understand if the
 * initially detected/selected location meets user needs.
 *
 * Triggered when: User changes location via the location selector after the page has loaded
 *
 * Data recorded:
 * - Previous location details (source, city, coordinates)
 * - New location details (source, city, coordinates)
 * - Distance between locations in miles
 * - IP-based location for comparison
 * - Event availability in each grid (primary, global, international)
 * - Country grid state (whether user is viewing country-specific events)
 * - Category information (ID and name of the performer/event category)
 *
 * This data helps answer questions like:
 * - How often do users change from their detected location?
 * - What distance do users typically travel when changing location?
 * - Are users in country grid mode more likely to change location?
 * - Does event availability correlate with location changes?
 */
export const sendLocationChangeUAE = ({ appContext, env, prevLocation, location, model }: LocationChangeUAEParams) => {
  const distanceInMiles = haversineDistance({
    locationA: { lat: prevLocation.lt, long: prevLocation.lg },
    locationB: { lat: location.lt, long: location.lg },
  });

  const { primaryGrid, restGrid: globalGrid } = getPerformerGridRoleSlots(model.performerGridSurface);
  const primaryGridEvents = primaryGrid?.totalCount ?? 0;

  const countryName = getPerformerPrimaryCountryName(model.performerGridSurface);
  const isInCountryGrid = !!countryName;
  const countryGridEvents = isInCountryGrid ? primaryGridEvents : null;

  const analyticsEventBody = {
    ...buildAnalyticsEvent(appContext, PerformerLocationAnalyticsEvents.LOCATION_CHANGE),
    categoryId: model.categorySummary.categoryId,
    categoryName: model.categorySummary.categoryName,
    prevLocationSource: prevLocation.src,
    prevCity: prevLocation.n,
    prevLat: prevLocation.lt,
    prevLong: prevLocation.lg,
    prevCity_Country: prevLocation.ct ?? null,
    prevCity_State: prevLocation.st ?? null,
    newLocationSource: location.src,
    newCity: location.n,
    newLat: location.lt,
    newLong: location.lg,
    newCity_Country: location.ct ?? null,
    newCity_State: location.st ?? null,
    ipCity: model.ipLocation?.cityName ?? null,
    ipLat: model.ipLocation?.latitude ?? null,
    ipLong: model.ipLocation?.longitude ?? null,
    ipCity_Country: model.ipLocation?.countryName ?? null,
    ipCity_State: model.ipLocation?.stateProvinceName ?? null,
    distanceInMiles: Math.round(distanceInMiles),
    primaryGridEvents,
    globalGridEvents: globalGrid?.totalCount ?? 0,
    isInCountryGrid,
    countryGridEvents,
    CountryGridName: countryName,
  };

  sendAnalyticsEvent(appContext, env, PerformerLocationAnalyticsEvents.LOCATION_CHANGE, [analyticsEventBody]);
};
